/**
 * International phone composition and the matching split-back.
 *
 * The booking flow stores the full international number ("+61 412…"), but
 * the forms ask for a dial prefix and a national part separately. A guest
 * who pastes or stores the whole number — with or without the "+" — must
 * not get the selected prefix prepended a second time, and a prefix that
 * differs from the address country must survive the round trip.
 */

/** The site is Australian-first — the phone prefix defaults to AU like legacy. */
export const DEFAULT_DIAL_CODE = '+61';

/**
 * Longest national significant number these forms accept as national. AU
 * national parts are nine digits and few countries exceed ten, so digits
 * longer than this that begin with a known dial code are an international
 * number the guest pasted without the "+", not a national number.
 */
const MAX_NATIONAL_DIGITS = 10;
/** Below this the digits cannot be a usable phone number at all. */
const MIN_NATIONAL_DIGITS = 4;

/** `00` (and Australia's `0011`) mean "+" — the same intent without the key. */
const INTL_ACCESS_GENERIC = '00';
const INTL_ACCESS_AU = '0011';

export interface SplitPhone {
  readonly dialCode: string;
  readonly national: string;
}

/** Longest dial-code match wins — "+1242" must beat "+1". */
function longestDialCodeMatch(digits: string, dialCodes: readonly string[]): string | undefined {
  let longest: string | undefined;
  for (const dialCode of dialCodes) {
    const code = dialCode.replace(/\D/g, '');
    if (
      code !== '' &&
      digits.startsWith(code) &&
      (longest === undefined || code.length > longest.length)
    ) {
      longest = code;
    }
  }
  return longest;
}

/**
 * Splits an international number into dial prefix + national part against
 * the known dial-code list — not just a hardcoded default. Unambiguous
 * inputs only: an explicit "+" always splits, digits without one only when
 * too long for a national number and beginning with a known prefix.
 *
 * A stored "+<prefix> <digits that are themselves international>" — a guest
 * who once typed the whole number into the national field — splits twice:
 * the inner digits are longer than any national number and begin with a
 * known prefix of their own, so the inner prefix wins.
 */
export function splitInternationalPhone(
  phone: string,
  dialCodes: readonly string[],
): SplitPhone | null {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!trimmed.startsWith('+') && digits.length <= MAX_NATIONAL_DIGITS) return null;
  const outer = longestDialCodeMatch(digits, dialCodes);
  if (outer === undefined) return null;
  let national = digits.slice(outer.length);
  if (national.length < MIN_NATIONAL_DIGITS) return null;
  let dialCode = `+${outer}`;
  if (national.length > MAX_NATIONAL_DIGITS) {
    const inner = longestDialCodeMatch(national, dialCodes);
    if (inner !== undefined && national.length - inner.length >= MIN_NATIONAL_DIGITS) {
      dialCode = `+${inner}`;
      national = national.slice(inner.length);
    }
  }
  return { dialCode, national };
}

/**
 * Composes the full international number the platform stores and sends to
 * the PMS. An explicit "+" is kept, "00"/"0011" is the same intent without
 * the key, digits too long for a national number that begin with a known
 * dial code are already international, and a local leading 0 drops in
 * favour of the selected prefix. `separator` preserves a stored format that
 * puts a space between the prefix and the number.
 */
export function composeInternationalPhone(
  input: string,
  selectedDialCode: string,
  dialCodes: readonly string[],
  separator = '',
): string {
  const join = (prefix: string, national: string): string => `${prefix}${separator}${national}`;
  const trimmed = input.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+')) {
    const split = splitInternationalPhone(trimmed, dialCodes);
    return split === null ? `+${digits}` : join(split.dialCode, split.national);
  }
  const access = digits.startsWith(INTL_ACCESS_AU)
    ? INTL_ACCESS_AU
    : digits.startsWith(INTL_ACCESS_GENERIC)
      ? INTL_ACCESS_GENERIC
      : '';
  const rest = access === '' ? digits : digits.slice(access.length);
  if (access !== '' && rest.length < MIN_NATIONAL_DIGITS) {
    return `+${rest}`;
  }
  const detected = splitInternationalPhone(rest, dialCodes);
  if (detected !== null) return join(detected.dialCode, detected.national);
  if (access !== '') return `+${rest}`;
  const national = rest.startsWith('0') ? rest.slice(1) : rest;
  return join(selectedDialCode, national);
}
